import { format, getDay } from "date-fns";
import { OTP_DELAY_MINUTES, 
    MAXIMUM_TAT_MINUTES, 
    FLIGHT_DATES,
} from 'state/constants';


const WEEKDAY_LABELS = ['D7. Sun', 'D1. Mon', 'D2. Tue', 'D3. Wed', 'D4. Thu', 'D5. Fri', 'D6. Sat'];

export function getETDRange(etd) {
    const [hour] = etd.split(':').map(Number);   
    const nextHour = (hour + 1) % 24;
    return `${String(hour).padStart(2, '0')} - ${String(nextHour).padStart(2, '0')}`;
}

export function countHeatmapByDelayCode(flights, timeMode = 'UTC', delayType = 'departure') {
    if (!flights || flights.length === 0) return [];

    const delayCodeByETD = flights.reduce((acc, flight) => {
        const etd = timeMode === 'UTC' ? flight.etd : flight.etdLocalBase; 
        const flightDelays = delayType === 'departure' ? flight.flightDepDelays : flight.flightArrDelays;

        if (!etd || !flightDelays) return acc;

        const etdRange = getETDRange(etd);  
        const arrFlightDelays = flightDelays.split(';');

        if (!acc[etdRange]) {
            acc[etdRange] = {};
        }

        arrFlightDelays.forEach(item => {
            const delayCode = item.trim().split(' ')[0];

            if (delayCode) {
                acc[etdRange][delayCode] = (acc[etdRange][delayCode] || 0) + 1;
            }
        });

        return acc;
    }, {});
    
    const transformedData = [];
    Object.keys(delayCodeByETD).forEach(etdRange => {
        const delayCounts = delayCodeByETD[etdRange];
        const dataEntries = Object.keys(delayCounts)
            .map(delayCode => ({
                x: delayCode.trim(),             
                y: delayCounts[delayCode] 
            }))
            .sort((a, b) => {
                const numA = parseInt(a.x, 10);
                const numB = parseInt(b.x, 10);
               
                if (isNaN(numA) || isNaN(numB)) {
                    return a.x.localeCompare(b.x); 
                }
                
                return numA - numB;
            });
        
        transformedData.push({
            id: etdRange,        
            data: dataEntries    
        });
    });

    transformedData.sort((a, b) => {
        const startA = parseInt(a.id.split(' - ')[0], 10); 
        const startB = parseInt(b.id.split(' - ')[0], 10); 
        return startA - startB; 
    });

    // 1. Extract all unique delay codes (x values)
    const allDelayCodes = Array.from(
        new Set(
            transformedData.flatMap(item => item.data.map(d => d.x))
        )
    );
    
    // 2. Sort delay codes (numeric first, then alpha)
    allDelayCodes.sort((a, b) => {
        const numA = parseInt(a, 10);
        const numB = parseInt(b, 10);
        const isNumA = !isNaN(numA);
        const isNumB = !isNaN(numB);
    
        if (isNumA && isNumB) return numA - numB;
        if (isNumA) return -1;
        if (isNumB) return 1;
    
        return a.localeCompare(b);
    });
    
    // 3. Reconstruct each row's `data` with sorted keys (ensuring 0 if missing)
    const normalizedData = transformedData.map(row => {
        const map = Object.fromEntries(row.data.map(d => [d.x, d.y]));
        return {
        id: row.id,
        data: allDelayCodes.map(code => ({
            x: code,
            y: map[code] || 0
        }))
        };
    });
  
    return normalizedData;
}

export function countHeatmapByDelayCode2(flights, timeMode = 'UTC') {
    if (!flights || flights.length === 0) return [];

    const delayCodeByETD = new Map();

    for (const flight of flights) {
        const etd = timeMode === 'UTC' ? flight.etd : flight.etdLocalBase;
        const delays = flight.flightDepDelays;

        if (!etd || !delays) continue;

        const etdRange = getETDRange(etd);
        const delayCodes = delays.split(';').map(d => d.trim().split(' ')[0]);

        if (!delayCodeByETD.has(etdRange)) {
            delayCodeByETD.set(etdRange, new Map());
        }

        const codeMap = delayCodeByETD.get(etdRange);
        for (const code of delayCodes) {
            if (code) {
                codeMap.set(code, (codeMap.get(code) || 0) + 1);
            }
        }
    }

    // Collect all unique delay codes
    const allDelayCodesSet = new Set();
    const rawData = [];

    for (const [etdRange, codeMap] of delayCodeByETD.entries()) {
        const row = {
            id: etdRange,
            data: []
        };

        for (const [code, count] of codeMap.entries()) {
            row.data.push({ x: code, y: count });
            allDelayCodesSet.add(code);
        }

        rawData.push(row);
    }

    // Sort delay codes: numeric first, then alpha
    const allDelayCodes = Array.from(allDelayCodesSet).sort((a, b) => {
        const numA = parseInt(a, 10);
        const numB = parseInt(b, 10);
        const isNumA = !isNaN(numA);
        const isNumB = !isNaN(numB);

        if (isNumA && isNumB) return numA - numB;
        if (isNumA) return -1;
        if (isNumB) return 1;

        return a.localeCompare(b);
    });

    // Normalize each row to have all sorted delay codes
    const normalizedData = rawData.map(row => {
        const codeMap = Object.fromEntries(row.data.map(d => [d.x, d.y]));
        return {
            id: row.id,
            data: allDelayCodes.map(code => ({
                x: code,
                y: codeMap[code] || 0
            }))
        };
    });

    // Sort rows by numeric start of range
    normalizedData.sort((a, b) => {
        const startA = parseInt(a.id.split(' - ')[0], 10);
        const startB = parseInt(b.id.split(' - ')[0], 10);
        return startA - startB;
    });

    return normalizedData;
}

export function calculateOTPByETDRange(flights, timeMode = 'UTC', delayType = 'departure', otpDelayMinutes = OTP_DELAY_MINUTES) {
    if (!flights || flights.length === 0) return [];

    const statsMap = new Map();

    for (const flight of flights) {
        const etd = timeMode === 'UTC' ? flight.etd : flight.etdLocalBase;
        const delayMinutes = delayType === 'departure' ? flight.depDelaysTime : flight.arrDelaysTime;
        const flightStatus = flight.flightStatus?.toLowerCase();

        if (!etd || !flightStatus || delayType === 'departure' 
            ? flightStatus.toLowerCase() === 'not yet departed'
            : flightStatus.toLowerCase() !== 'arrived') {

            continue;
        }

        const etdRange = getETDRange(etd);

        if (!statsMap.has(etdRange)) {
            statsMap.set(etdRange, { total: 0, delayed: 0 });
        }

        const stats = statsMap.get(etdRange);
        stats.total += 1;
        if (delayMinutes > otpDelayMinutes) {
            stats.delayed += 1;
        }

        statsMap.set(etdRange, stats);
    }

    const sortedRanges = [...statsMap.entries()].sort((a, b) => {
        const startA = parseInt(a[0].split(' - ')[0], 10);
        const startB = parseInt(b[0].split(' - ')[0], 10);
        return startA - startB;
    });

    // const arrDelayCount = sortedRanges.map(([etdRange, stats]) => ({
    //     x: etdRange,
    //     y: stats.delayed
    // }));

    const arrOTP = sortedRanges.map(([etdRange, stats]) => ({
        x: etdRange,
        y: stats.total > 0
            ? parseFloat(((1 - stats.delayed / stats.total) * 100).toFixed(1))
            : 100
    }));

    const totalDelayed = [...statsMap.values()].reduce((sum, s) => sum + s.delayed, 0);
    const totalFlights = [...statsMap.values()].reduce((sum, s) => sum + s.total, 0);
    const otpAverage = totalFlights > 0
        ? parseFloat(((1 - totalDelayed / totalFlights) * 100).toFixed(1))
        : 100;

    const arrOtpAverage = arrOTP?.map(({ x }) => ({
        x,
        y: otpAverage
    }));

    return [        
        {
            id: "OTP",
            color: "hsl(120, 70%, 50%)",
            data: arrOTP
        },
        {
            id: "OTPAvg",
            color: "hsl(200, 70%, 50%)",
            data: arrOtpAverage
        },
        // {
        //     id: "Delay Count",
        //     color: "#f44336",
        //     data: arrDelayCount
        // },
    ];
} 

export function calculateDelaysTimeByETDRange(flights, timeMode = 'UTC', delayType = 'departure', otpDelayMinutes = OTP_DELAY_MINUTES) {
    if (!flights || flights.length === 0) return [];

    const delayStatsMap = new Map();

    for (const flight of flights) {
        const etd = timeMode === 'UTC' ? flight.etd : flight.etdLocalBase;
        const delayMinutes = delayType === 'departure' ? flight.depDelaysTime : flight.arrDelaysTime;

        if (!etd) continue;

        const etdRange = getETDRange(etd);

        if (!delayStatsMap.has(etdRange)) {
            delayStatsMap.set(etdRange, { total: 0, count: 0 });
        }

        const stats = delayStatsMap.get(etdRange);
        if (delayMinutes > otpDelayMinutes) {
            stats.total += delayMinutes;
            stats.count += 1;
        }        

        delayStatsMap.set(etdRange, stats);
    }

    const sortedRanges = [...delayStatsMap.entries()].sort((a, b) => {
        const startA = parseInt(a[0].split(' - ')[0], 10);
        const startB = parseInt(b[0].split(' - ')[0], 10);
        return startA - startB;
    });

    const averageDelays = sortedRanges.map(([etdRange, { total, count }]) => ({
        x: etdRange,
        y: count > 0 ? parseFloat((total / count).toFixed(1)) : 0,
    }));

    const totalDelays = [...delayStatsMap.values()].reduce((sum, s) => sum + s.total, 0);
    const totalCount = [...delayStatsMap.values()].reduce((sum, s) => sum + s.count, 0);
    const overallAvg = totalCount > 0 ? parseFloat((totalDelays / totalCount).toFixed(1)) : 0;

    const averageLine = averageDelays.map(({ x }) => ({
        x,
        y: overallAvg
    }));

    return [
        {
            id: 'DelaysTime',
            color: "hsl(120, 70%, 50%)",
            data: averageDelays
        },
        {
            id: 'Average',
            color: 'hsl(200, 70%, 50%)',
            data: averageLine
        }
    ];
}

export function calculateOTPByWeekday(flights, timeMode = 'UTC', delayType = 'departure', otpDelayMinutes = OTP_DELAY_MINUTES) {
    if (!flights || flights.length === 0) return [];

    const statsMap = new Map();

    for (const flight of flights) {
        const etd = timeMode === 'UTC' ? flight.fullEtd : flight.fullEtdLocalBase;
        const delayMinutes = delayType === 'departure' ? flight.depDelaysTime : flight.arrDelaysTime;
        const flightStatus = flight.flightStatus?.toLowerCase();

        if (!etd || !flightStatus || delayType === 'departure' 
            ? flightStatus.toLowerCase() === 'not yet departed'
            : flightStatus.toLowerCase() !== 'arrived') {

            continue;
        }

        const weekdayIndex = getDay(new Date(etd)); // 0 = Sun
        const weekdayLabel = WEEKDAY_LABELS[weekdayIndex];

        if (!statsMap.has(weekdayLabel)) {
            statsMap.set(weekdayLabel, { total: 0, delayed: 0 });
        }

        const stats = statsMap.get(weekdayLabel);
        stats.total += 1;
        if (delayMinutes > otpDelayMinutes) {
            stats.delayed += 1;
        }
    }

    const arrOTP = WEEKDAY_LABELS.map(day => {
        const stats = statsMap.get(day) || { total: 0, delayed: 0 };
        return {
            x: day,
            y: stats.total > 0
                ? parseFloat(((1 - stats.delayed / stats.total) * 100).toFixed(1))
                : 100
        };
    });

    const totalStats = [...statsMap.values()].reduce(
        (acc, { total, delayed }) => {
            acc.total += total;
            acc.delayed += delayed;
            return acc;
        },
        { total: 0, delayed: 0 }
    );

    const otpAverage = totalStats.total > 0
        ? parseFloat(((1 - totalStats.delayed / totalStats.total) * 100).toFixed(1))
        : 100;

    const otpAverageLine = arrOTP.map(({ x }) => ({
        x,
        y: otpAverage
    }));

    return [
        {
            id: 'OTP',
            color: 'hsl(120, 70%, 50%)',
            data: arrOTP
        },
        {
            id: 'OTPAvg',
            color: 'hsl(200, 70%, 50%)',
            data: otpAverageLine
        }
    ];
}

export function calculateDelaysTimeByWeekday(flights, timeMode = 'UTC', delayType = 'departure', otpDelayMinutes = OTP_DELAY_MINUTES) {
    if (!flights || flights.length === 0) return [];

    const statsMap = new Map();

    for (const flight of flights) {
        const etd = timeMode === 'UTC' ? flight.fullEtd : flight.fullEtdLocalBase;
        const delayMinutes = delayType === 'departure' ? flight.depDelaysTime : flight.arrDelaysTime;

        if (!etd || delayMinutes == null) continue;

        const weekdayIndex = getDay(new Date(etd));
        const weekdayLabel = WEEKDAY_LABELS[weekdayIndex];

        if (!statsMap.has(weekdayLabel)) {
            statsMap.set(weekdayLabel, { total: 0, count: 0 });
        }

        if (delayMinutes > otpDelayMinutes) {
            const stats = statsMap.get(weekdayLabel);
            stats.total += delayMinutes;
            stats.count += 1;
        }
    }
    
    const averageDelays = WEEKDAY_LABELS.map(day => {
        const stats = statsMap.get(day) || { total: 0, count: 0 };
        return {
            x: day,
            y: stats.count > 0 ? parseFloat((stats.total / stats.count).toFixed(1)) : 0
        };
    });
    
    const { total: totalDelays, count: totalCount } = [...statsMap.values()].reduce(
        (acc, { total, count }) => {
            acc.total += total;
            acc.count += count;
            return acc;
        },
        { total: 0, count: 0 }
    );

    const overallAvg = totalCount > 0 ? parseFloat((totalDelays / totalCount).toFixed(1)) : 0;

    const averageLine = averageDelays.map(({ x }) => ({
        x,
        y: overallAvg
    }));

    return [
        {
            id: 'DelaysTime',
            color: "hsl(120, 70%, 50%)",
            data: averageDelays
        },
        {
            id: 'Average',
            color: 'hsl(200, 70%, 50%)',
            data: averageLine
        }
    ];
}

export function countPaxAffectedByDelay(flights, fieldName, delayType = 'departure', chartType, otpDelayMinutes = OTP_DELAY_MINUTES) {    
    if (!flights || flights.length === 0) return [];

    const obj = flights.reduce((acc, flight) => {
        const key = flight[fieldName] || 'N/A';

        const delayMinutes = delayType === 'departure' ? flight.depDelaysTime : flight.arrDelaysTime;
        const flightStatus = (flight.flightStatus || "").toLowerCase();
       
        if (delayType === 'departure' 
            ? flightStatus !== 'not yet departed'
            : flightStatus === 'arrived') {
            if (!acc[key]) {
                acc[key] = { totalFlights: 0, delayedFlights: 0, affectedPax: 0 };
            }

            // Increment total flights count
            acc[key].totalFlights += 1;
            if (delayMinutes > otpDelayMinutes) {
                acc[key].delayedFlights += 1;
                acc[key].affectedPax += Math.min(flight.noOfPax, flight.capacity);
            }
        }

        return acc;
    }, {});

    if (Object.keys(obj).length === 0) return [];

    // Sorting function
    const sortCriteria = (a, b) => 
        FLIGHT_DATES.includes(fieldName)
            ? new Date(a.x) - new Date(b.x)  
            : a.x.localeCompare(b.x);

    // Create arrays for Flights
    const arrAffectedPaxs = Object.entries(obj).map(([key, value]) => ({
        x: key, 
        y: value.affectedPax
    })).sort(sortCriteria);

    const totalPax = arrAffectedPaxs.reduce((acc, cur) => acc + cur.y, 0);
    const paxAverage = parseFloat((totalPax / arrAffectedPaxs.length).toFixed(0));       
    const arrPaxAverage = arrAffectedPaxs.map(({ x }) => ({ x, y: paxAverage }));

    const barLinesChartData = [        
        { id: 'Pax', color: 'hsl(120, 70%, 50%)', data: arrAffectedPaxs },        
        { id: 'PaxAvg', color: 'hsl(200, 70%, 50%)', data: arrPaxAverage },        
    ];

    const arrTimeRangeData = Object.entries(obj).map(([key, value]) => ({
        value: value.totalFlights > 0 ? value.affectedPax / value.totalFlights : 0,
        day: FLIGHT_DATES.includes(fieldName)
            ? format(key, 'yyyy-MM-dd')  
            : null
    }));

    switch (chartType) {
        case 'bar':
        case 'column':
            return arrAffectedPaxs;        
        case 'timerange':
            return arrTimeRangeData;
        case 'barlines':
            return barLinesChartData;
        default:
            return barLinesChartData;            
    }
}

export function countPaxAffectedByDepOTP(flights, delayType = 'departure', chartType, otpDelayMinutes = OTP_DELAY_MINUTES) {
    const objOTP = flights?.reduce((acc, flight) => {       
        const delayMinutes = delayType === 'departure' ? flight.depDelaysTime : flight.arrDelaysTime;
        const flightStatus = flight.flightStatus.toLowerCase();

        if (delayType === 'departure' 
            ? flightStatus.toLowerCase() === 'not yet departed'
            : flightStatus.toLowerCase() !== 'arrived') {
                
            return acc;
        }

        if (!acc.totalPax) {
            acc.totalPax = 0;
            acc.delayedPax = 0;
            acc.ontimePax = 0;
        }

        acc.totalPax += Math.min(flight.noOfPax, flight.capacity);
        if (delayMinutes > otpDelayMinutes) {
            acc.delayedPax += Math.min(flight.noOfPax, flight.capacity);
        } else {
            acc.ontimePax += Math.min(flight.noOfPax, flight.capacity);
        }

        return acc;
    }, {}); 
    
    if (Object.keys(objOTP).length === 0) return [];    
        
    switch (chartType) {
        case 'bar':
        case 'column':
            const barChartData = [
                { x: 'Pax total', y: objOTP.totalPax },
                { x: 'Pax on time', y: objOTP.ontimePax },
                { x: 'Pax delayed', y: objOTP.delayedPax }
            ];
            return barChartData;
        case 'pie':            
            const pieChartData = Object.entries({
                'Pax on time': objOTP.ontimePax,
                'Pax delayed': objOTP.delayedPax
            }).map(([key, value]) => ({
                id: key,
                label: key,
                value
            }));
            return pieChartData;
        default:
            return barChartData;            
    }
}

export function countOTPOverall(flights, delayType = 'departure', chartType, otpDelayMinutes = OTP_DELAY_MINUTES) {
    const objOTP = flights?.reduce((acc, flight) => {       
        const delayMinutes = delayType === 'departure' ? flight.depDelaysTime : flight.arrDelaysTime;
        const flightStatus = flight.flightStatus.toLowerCase();

        if (delayType === 'departure' 
            ? flightStatus.toLowerCase() === 'not yet departed'
            : flightStatus.toLowerCase() !== 'arrived') {

            return acc;
        }

        if (!acc.totalFlights) {
            acc.totalFlights = 0;
            acc.delayedFlights = 0;
            acc.ontimeFlights = 0;
        }

        acc.totalFlights += 1;
        if (delayMinutes > otpDelayMinutes) {
            acc.delayedFlights += 1;
        } else {
            acc.ontimeFlights += 1;
        }

        return acc;
    }, {}); 
    
    if (Object.keys(objOTP).length === 0) return [];    
        
    switch (chartType) {
        case 'bar':
        case 'column':
            const barChartData = [
                { x: 'Total flown', y: objOTP.totalFlights },
                { x: 'On time', y: objOTP.ontimeFlights },
                { x: 'Delayed', y: objOTP.delayedFlights }
            ];
            return barChartData;
        case 'pie':            
            const pieChartData = Object.entries({
                'On time': objOTP.ontimeFlights,
                'Delayed': objOTP.delayedFlights
            }).map(([key, value]) => ({
                id: key,
                label: key,
                value
            }));
            return pieChartData;
        default:
            return barChartData;            
    }
}

export function countDepOTPOverallAffectedByArrDelay(flights, delayType = 'departure', chartType, otpDelayMinutes = OTP_DELAY_MINUTES) {
    const obj = flights?.reduce((acc, flight) => {       
        const delayMinutes = delayType === 'departure' ? flight.depDelaysTime : flight.arrDelaysTime;
        const preArrDelaysTime = flight.preArrDelaysTime;
        const flightStatus = flight.flightStatus.toLowerCase();

        if (delayType === 'departure' 
            ? flightStatus.toLowerCase() === 'not yet departed'
            : flightStatus.toLowerCase() !== 'arrived') {

            return acc;
        }

        if (!acc.totalFlights) {
            acc.totalFlights = 0;
            acc.depDelayedFlights = 0;
            acc.arrDelayedFlights = 0;
        }

        acc.totalFlights += 1;
        if (delayMinutes > otpDelayMinutes) {
            acc.depDelayedFlights += 1;

            if (preArrDelaysTime > otpDelayMinutes) {
                acc.arrDelayedFlights += 1;
            } 
        } 

        return acc;
    }, {}); 
    
    if (Object.keys(obj).length === 0) return [];    
        
    if (chartType === 'pie') {
        const pieChartData = Object.entries({
            'Previous arrival delays': obj.arrDelayedFlights,
            'Other reasons': obj.depDelayedFlights - obj.arrDelayedFlights
        }).map(([key, value]) => ({
            id: key,
            label: key,
            value
        }));

        return pieChartData;
    } else {
        const barChartData = [
            { x: 'Departure delays', y: obj.depDelayedFlights },
            { x: 'Previous arrival delays', y: obj.arrDelayedFlights },
            { x: 'Other reasons', y: obj.depDelayedFlights - obj.arrDelayedFlights }
        ];

        return barChartData;
    }
}

export function countOTPByField(flights, fieldName, delayType = 'departure', chartType, otpDelayMinutes = OTP_DELAY_MINUTES) {    
    const obj = flights.reduce((acc, flight) => {
        const key = flight[fieldName] || 'N/A';

        const delayMinutes = delayType === 'departure' ? flight.depDelaysTime : flight.arrDelaysTime;
        const flightStatus = (flight.flightStatus || "").toLowerCase();
       
        if (delayType === 'departure' 
            ? flightStatus !== 'not yet departed'
            : flightStatus === 'arrived') {
            if (!acc[key]) {
                acc[key] = { totalFlights: 0, delayedFlights: 0 };
            }

            // Increment total flights count
            acc[key].totalFlights += 1;
            if (delayMinutes > otpDelayMinutes) {
                acc[key].delayedFlights += 1;
            }
        }

        return acc;
    }, {});

    if (Object.keys(obj).length === 0) return [];

    // Sorting function
    const sortCriteria = (a, b) => 
        FLIGHT_DATES.includes(fieldName)
            ? new Date(a.x) - new Date(b.x)  
            : a.x.localeCompare(b.x);

    // Create arrays for Flights
    const arrFlights = Object.entries(obj).map(([key, value]) => ({
        x: key, 
        y: value.totalFlights
    }))?.sort(sortCriteria);

    const totalFlights = arrFlights.reduce((acc, cur) => acc + cur.y, 0);
    // const flightsAverage = parseFloat((totalFlights / arrFlights.length).toFixed(0));       
    // const arrFlightsAverage = arrFlights.map(({ x }) => ({ x, y: flightsAverage }));


    // Create arrays for OTP
    const arrOTP = Object.entries(obj).map(([key, value]) => ({
        x: key,
        y: value.totalFlights > 0 ? parseFloat(((1 - value.delayedFlights / value.totalFlights) * 100).toFixed(1)) : 100
    }))?.sort(sortCriteria);

    const totalDelayedFlights = Object.values(obj).reduce((acc, { delayedFlights }) => acc + delayedFlights, 0);
    const otpAverage = totalFlights > 0 ? parseFloat(((1 - totalDelayedFlights / totalFlights) * 100).toFixed(1)) : 0;
    const arrOTPAverage = arrOTP.map(({ x }) => ({
        x,
        y: otpAverage
    }));
    
    
    const barLinesChartData = [        
        { id: 'OTP', color: 'hsl(120, 70%, 50%)', data: arrOTP },        
        { id: 'OTPAvg', color: 'hsl(200, 70%, 50%)', data: arrOTPAverage },
        // { id: 'Flt', color: 'hsla(300, 34.80%, 74.10%, 0.88)', data: arrFlights, isAllPointsTextVisible: true },
        // { id: 'FltAvg', color: 'hsl(56, 62.40%, 55.10%)', data: arrFlightsAverage },
    ];

    const arrTimeRangeData = Object.entries(obj).map(([key, value]) => ({
        value: value.totalFlights > 0 ? parseFloat(((1 - value.delayedFlights / value.totalFlights) * 100).toFixed(1)) : 100,
        day: FLIGHT_DATES.includes(fieldName)
            ? format(key, 'yyyy-MM-dd')  
            : null
    }));
    
    switch (chartType) {
        case 'bar':
        case 'column':
            return arrOTP;
        case 'pie':            
            return Object.entries(obj).map(([key, {totalFlights, delayedFlights}]) => ({
                id: key,
                label: key,
                // value: totalFlights > 0 ? parseFloat(((1 - delayedFlights / totalFlights) * 100).toFixed(1)) : 100,
                value: delayedFlights,
            }));
        case 'timerange':
            return arrTimeRangeData;
        case 'barlines':
            return barLinesChartData;
        default:
            return barLinesChartData;            
    }
}

export function countOTPBy2Fields(flights, field1, field2, delayType = 'departure', otpDelayMinutes = OTP_DELAY_MINUTES) {   
    if (!flights) return null;
    
    const objValues = flights.reduce((acc, flight) => {
        const key = field2 
            ? `${flight[field1]}|${flight[field2]}`
            : `${flight[field1]}|Value`;

        const delayMinutes = delayType === 'departure' ? flight.depDelaysTime : flight.arrDelaysTime;
        const flightStatus = (flight.flightStatus || "").toLowerCase();
       
        if (delayType === 'departure' 
            ? flightStatus !== 'not yet departed'
            : flightStatus === 'arrived') {
            if (!acc[key]) {
                acc[key] = { 
                    totalFlights: 0, 
                    delayedFlights: 0, 
                    flights: [],
                };
            }

            // Increment total flights count
            acc[key].totalFlights += 1;
            if (delayMinutes > otpDelayMinutes) {
                acc[key].delayedFlights += 1;
            }     

            acc[key].flights.push({
                flightNo: flight.flightNo,
                flightDate: flight.flightDate,
                flightDateLocalBase: flight.flightDateLocalBase,
                sector: flight.sector,
                std: flight.std,
                sta: flight.sta,
                atd: flight.atd,
                ata: flight.ata,
                stdLocalBase: flight.stdLocalBase,
                staLocalBase: flight.staLocalBase,
                atdLocalBase: flight.atdLocalBase,
                ataLocalBase: flight.ataLocalBase,                
                otp: delayMinutes > otpDelayMinutes ? 'Delayed' : 'Ontime',
            });       
        }

        return acc;
    }, {});

    if (Object.keys(objValues).length === 0) return [];

    const arrValues = Object.entries(objValues)
        .map(([key, { totalFlights, delayedFlights, flights }]) => ({
            key,
            otp: totalFlights > 0 
                ? Math.min(100, parseFloat(((1 - delayedFlights / totalFlights) * 100).toFixed(1))) 
                : 100,
            delayedFlights,
            totalFlights,
            flights,
        }))
        .sort((a, b) => {
            const [partA] = a.key.split("|");
            const [partB] = b.key.split("|");
            const dateA = Date.parse(partA);
            const dateB = Date.parse(partB);

            if (!isNaN(dateA) && !isNaN(dateB)) {
                return dateA - dateB;
            }
            return a.key.localeCompare(b.key);
        });
    
    return arrValues;
}

export function countOTPAffectedByArrDelay(flights, fieldName, delayType = 'departure', chartType, otpDelayMinutes = OTP_DELAY_MINUTES) {    
    const obj = flights.reduce((acc, cur) => {
        const key = cur[fieldName] || 'N/A';

        const delayMinutes = delayType === 'departure' ? cur.depDelaysTime : cur.arrDelaysTime;
        const preArrDelaysTime = cur.preArrDelaysTime;
        const flightStatus = (cur.flightStatus || "").toLowerCase();
       
        if (delayType === 'departure' 
            ? flightStatus !== 'not yet departed'
            : flightStatus === 'arrived') {

            if (!acc[key]) {
                acc[key] = { totalFlights: 0, delayedFlights: 0, arrDelayedFlights: 0 };
            }
            
            acc[key].totalFlights += 1;
            if (delayMinutes > otpDelayMinutes) {
                acc[key].delayedFlights += 1;

                if (preArrDelaysTime > otpDelayMinutes) {
                    acc[key].arrDelayedFlights += 1;
                }
            }            
        }

        return acc;
    }, {});

    if (Object.keys(obj).length === 0) return [];

    // Sorting function
    const sortCriteria = (a, b) => 
        FLIGHT_DATES.includes(fieldName)
            ? new Date(a.x) - new Date(b.x)  
            : a.x.localeCompare(b.x);


    const columnChartData = Object.entries(obj).map(([key, value]) => {
        const OTP = value.totalFlights ? parseFloat((1 - value.delayedFlights / value.totalFlights) * 100) : 100;
        const preArrOTP = value.totalFlights ? parseFloat((1 - value.arrDelayedFlights / value.totalFlights) * 100) : 100;          
        
        return {
            x: key,
            OTP, 
            preArrOTP,
        };
    }).sort(sortCriteria);

    
    const departureOTP = Object.entries(obj).map(([key, value]) => ({
        x: key,
        y: value.totalFlights > 0 ? parseFloat(((1 - value.delayedFlights / value.totalFlights) * 100).toFixed(1)) : 100
    }))
    .sort(sortCriteria);
    
    const arrivalDelayEffect = Object.entries(obj).map(([key, value]) => ({
        x: key,
        y: value.delayedFlights > 0 ? ((value.arrDelayedFlights / value.delayedFlights) * 100) : 100
    }))
    .sort(sortCriteria);
    
    
    const totalDelayedFlights = Object.values(obj).reduce((acc, { delayedFlights }) => acc + delayedFlights, 0);
    const totalArrDelayedFlights = Object.values(obj).reduce((acc, { arrDelayedFlights }) => acc + arrDelayedFlights, 0);
    const effectAverage = totalDelayedFlights > 0 ? ((totalArrDelayedFlights / totalDelayedFlights) * 100) : 0;
    const arrivalDelayAverage = arrivalDelayEffect.map(({ x }) => ({
        x,
        y: effectAverage
    }));
    

    const barLinesChartData = [        
        { id: 'OTP', color: 'hsl(120, 70%, 50%)', data: departureOTP },        
        { id: 'Affected by arr delay', color: 'hsl(200, 70%, 50%)', data: arrivalDelayEffect, isAllPointsTextVisible: true },
        { id: 'Average', color: 'hsl(58, 77.10%, 58.80%)', data: arrivalDelayAverage },
    ];

    switch (chartType) {
        case 'bar':
        case 'column':
            return columnChartData;
        case 'barlines':
            return barLinesChartData;
        default:
            return barLinesChartData;            
    }
}
export function countDelayPropagationMetrics(
    flights,
    fieldName,
    otpDelayMinutes = OTP_DELAY_MINUTES
) {

    const grouped = flights.reduce((acc, flight) => {

        const key = flight[fieldName] || 'N/A';
        const depDelay = Math.max(flight.depDelaysTime || 0, 0);
        const arrDelay = Math.max(flight.preArrDelaysTime || 0, 0);
        const flightStatus = (flight.flightStatus || "").toLowerCase();

        if (flightStatus === 'not yet departed') return acc;

        if (!acc[key]) {
            acc[key] = {
                totalFlights: 0,

                // AR
                totalAR: 0,
                validARFlights: 0,

                // PI
                totalPI: 0,
                validPIFlights: 0,

                // Severity (weighted PI)
                sumDepDelay: 0,
                sumArrDelay: 0
            };
        }

        const bucket = acc[key];
        bucket.totalFlights += 1;

        if (arrDelay > otpDelayMinutes && flight.tat < MAXIMUM_TAT_MINUTES) {

            // --- Propagation Index ---
            const PI = depDelay / arrDelay;
            bucket.totalPI += PI;
            bucket.validPIFlights += 1;

            // --- Absorption Rate ---
            const AR = (arrDelay - depDelay) / arrDelay;
            bucket.totalAR += AR;
            bucket.validARFlights += 1;

            // --- Weighted Severity ---
            bucket.sumDepDelay += depDelay;
            bucket.sumArrDelay += arrDelay;
        }

        return acc;

    }, {});


    if (Object.keys(grouped).length === 0) return [];


    const sortCriteria = (a, b) =>
        FLIGHT_DATES.includes(fieldName)
            ? new Date(a.x) - new Date(b.x)
            : a.x.localeCompare(b.x);


    const result = Object.entries(grouped).map(([key, value]) => {

        const avgPI = value.validPIFlights > 0
            ? parseFloat((value.totalPI / value.validPIFlights).toFixed(2))
            : 0;

        // const avgAR = value.validARFlights > 0
        //     ? parseFloat((value.totalAR / value.validARFlights).toFixed(2))
        //     : 0;

        // const weightedPI = value.sumArrDelay > 0
        //     ? parseFloat((value.sumDepDelay / value.sumArrDelay).toFixed(2))
        //     : 0;

        return {
            x: key,
            y: avgPI,
            // AR: avgAR,
            // severityPI: weightedPI
        };

    }).sort(sortCriteria);


    return result;
}


export function countDepOTPByDelayCode(flights, delayType = 'departure') {    
    const objDelayCodes = flights.reduce((acc, flight) => {
        const flightDelays = delayType === 'departure' ? flight.flightDepDelays : flight.flightArrDelays;

        if (flightDelays) {
            const arrFlightDelays = flightDelays.split(';');
            
            arrFlightDelays.forEach(item => {
                const delayCode = item.trim().split(' ')[0];
                
                if (delayCode) {
                    acc[delayCode] = (acc[delayCode] || 0) + 1; 
                }                
            });
        }
        
        return acc;
    }, {});
    
    if (Object.keys(objDelayCodes).length === 0) return [];    
    const arrDelayCode = [];

    Object.keys(objDelayCodes).forEach(code => {
        arrDelayCode.push({ delayCode: code, count: objDelayCodes[code] });
    });
    
    const chartData = arrDelayCode.map(item => ({
        x: item.delayCode,
        y: item.count,
    }));

    return chartData;
}

export function countOTPByFieldStackedBarChart(flights, fieldName, delayType = 'departure', otpDelayMinutes = OTP_DELAY_MINUTES) {    
    const objOTP = flights.reduce((acc, flight) => {
        const key = flight[fieldName] || 'N/A';

        const delayMinutes = delayType === 'departure' ? flight.depDelaysTime : flight.arrDelaysTime;
        const flightStatus = (flight.flightStatus || "").toLowerCase();
       
        if (delayType === 'departure' 
            ? flightStatus !== 'not yet departed'
            : flightStatus === 'arrived') {
            if (!acc[key]) {
                acc[key] = { totalFlights: 0, delayedFlights: 0, ontimeFlights: 0 };
            }

            acc[key].totalFlights += 1;
            if (delayMinutes > otpDelayMinutes) {
                acc[key].delayedFlights += 1;
            } else {
                acc[key].ontimeFlights += 1;
            }
        }

        return acc;
    }, {});

    if (Object.keys(objOTP).length === 0) return [];
  
    const sortCriteria = (a, b) => 
        FLIGHT_DATES.includes(fieldName)
            ? new Date(a.x) - new Date(b.x)  
            : a.x.localeCompare(b.x);
            
    const chartData = Object.entries(objOTP)
        .map(([key, value]) => ({
            x: key,
            Ontime: value.ontimeFlights,
            Delay: value.delayedFlights,
        }))
        .sort(sortCriteria);
      
    return chartData;
}
